import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, formatFxRate, formatMoney, formatQuantity, fxRate, type Centavos } from '@abastece/shared';
import {
  accountByCode,
  balanceGeneral,
  cuadra,
  estadoResultados,
  libroCaja,
  totalUtilidad,
  utilidadPorCliente,
  utilidadPorProducto,
  type Cuadre,
  type FilaUtilidad,
  type LineaReporte,
} from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { useCuentasPuestas } from '../lib/bancos.js';
import { filasDichas } from '../lib/analisis.js';
import { etiquetaRango } from '../lib/historial.js';
import {
  comoRango,
  cuadresLocales,
  dineroDeLosLibros,
  elegirMonedaDeLosLibros,
  fiadoDelPeriodo,
  libroDelPeriodo,
  monedaDeLosLibros,
  periodos,
  ventasDelPeriodo,
  type BusquedaContabilidad,
  type DineroDeLosLibros,
  type MonedaDeLosLibros,
  type PeriodoContabilidad,
} from '../lib/libros.js';
import { usePuede } from '../lib/sesion.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { AporteDelDueno } from '../ui/AporteDelDueno.js';
import { ElegirMoneda } from '../ui/ElegirMoneda.js';
import { ConMarca, Explicacion, type PasoDelCalculo } from '../ui/Explicacion.js';
import { Calendario } from '../ui/FiltrosHistorial.js';
import { MoverDinero } from '../ui/MoverDinero.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { etiquetaCuenta, explicacionCuenta } from '../i18n/dominio.js';
import { useHoy } from '../lib/hoy.js';

type Vista = 'resultados' | 'balance' | 'caja' | 'utilidad';

const VISTAS: { clave: Vista; nombre: Clave }[] = [
  { clave: 'resultados', nombre: 'conta.resultados' },
  { clave: 'balance', nombre: 'conta.balance' },
  { clave: 'caja', nombre: 'conta.caja' },
  { clave: 'utilidad', nombre: 'conta.utilidad' },
];

const pct = (bp: number | null) => (bp === null ? '—' : `${(bp / 100).toFixed(1)}%`);

/** How many of the cash book's movements show at a time, the latest first. */
const LOTE_CAJA = 200;
/** How many rows a profit table shows at a time: it showed a hundred, and nothing said there were more. */
const LOTE_UTILIDAD = 100;

export function Contabilidad() {
  const t = useT();
  /** The owner's names for the bank accounts, where the books list them. */
  const bancos = useCuentasPuestas();
  const nombre = useNombreProducto();
  const puedeVer = usePuede('ver_reportes');
  /** Cash to and from the bank: whoever holds the safe. Money the owner puts in: the owner's own. */
  const puedeMover = usePuede('recibir_efectivo');
  const puedeAportar = usePuede('configurar');
  /** The two sheets of the books' own (the owner, 2026-10-07): moving cash, and the owner putting money in. */
  const [hoja, setHoja] = useState<'mover' | 'aporte' | null>(null);
  /**
   * The tab and the days, from the address (`busquedaDeContabilidad`): a named period, or days of the
   * owner's own besides them (the owner, 2026-10-13: «the accounting page should have a date range
   * picker as well»), picked on the calendar the histories use. Changed in place, with no step for
   * «Volver» to undo (`lib/volver.ts`).
   */
  const busqueda = useSearch({ from: '/contabilidad' });
  const navigate = useNavigate();
  const ir = (b: BusquedaContabilidad) => void navigate({ to: '/contabilidad', search: b, replace: true });
  const vista: Vista = busqueda.vista ?? 'resultados';
  const conVista = busqueda.vista ? { vista: busqueda.vista } : {};
  const elegidas = busqueda.desde && busqueda.hasta ? { desde: busqueda.desde, hasta: busqueda.hasta } : null;
  const conDias = elegidas ?? (busqueda.periodo ? { periodo: busqueda.periodo } : {});
  const elegirPeriodo = (clave: PeriodoContabilidad) => ir({ ...conVista, ...(clave === 'esteMes' ? {} : { periodo: clave }) });
  const setElegidas = (dias: { desde: string; hasta: string }) => ir({ ...conVista, ...dias });
  const setVista = (v: Vista) => ir({ ...conDias, ...(v === 'resultados' ? {} : { vista: v }) });
  const [calendario, setCalendario] = useState(false);
  /**
   * The cash book's latest two hundred, and «Ver más» for the rest: it used to stop there, and a
   * busy month's first days could not be read at all.
   */
  const [deCaja, setDeCaja] = useState(LOTE_CAJA);
  /** Córdobas or dollars, every figure on the page: the books themselves stay in córdobas. */
  const [moneda, setMoneda] = useState<MonedaDeLosLibros>(monedaDeLosLibros);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** The rate the books are kept at (`negocio.tasaOficialAyuda`), so the one they are read in. */
  const tasa = fxRate(config.fxTasaOficial);
  const dinero = useMemo(() => dineroDeLosLibros(moneda, tasa), [moneda, tasa]);
  const elegirMoneda = (m: MonedaDeLosLibros) => {
    setMoneda(m);
    elegirMonedaDeLosLibros(m);
  };

  // A screen left open over midnight offered yesterday's month as this one (`useHoy`).
  const hoyEs = useHoy();
  const opciones = useMemo(() => periodos(), [hoyEs]);
  const locale = localeActual();
  const claveDe = (p: { etiqueta: string }) => p.etiqueta.slice('periodo.'.length) as PeriodoContabilidad;
  const indice = Math.max(0, opciones.findIndex((p) => claveDe(p) === (busqueda.periodo ?? 'esteMes')));
  const periodo = elegidas ?? opciones[indice]!;
  const rango = comoRango(periodo);
  /** The days the figures are of, as their button says them: «22 ago – 1 sep». */
  const dias = elegidas ? etiquetaRango(elegidas.desde, elegidas.hasta, hoyEs, locale) : null;

  // The period's own entries and sales, and before it each account's days added up: what the
  // reports read, found by date, rather than every entry and sale the shop ever made.
  const asientos = useLiveQuery(() => libroDelPeriodo(rango), [periodo], []);
  const ventas = useLiveQuery(() => ventasDelPeriodo(rango), [periodo], []);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const clientes = useLiveQuery(() => db.clientes.toArray(), [], []);

  const resultados = useMemo(() => estadoResultados(asientos, rango), [asientos, periodo]);
  const balance = useMemo(
    () => balanceGeneral(asientos, periodo.hasta || undefined),
    [asientos, periodo],
  );
  const caja = useMemo(() => libroCaja(asientos, rango), [asientos, periodo]);
  const fiado = useMemo(() => fiadoDelPeriodo(asientos, rango), [asientos, periodo]);
  // Today's, whatever the period: the clients and the shelves are only ever as they are now.
  const cuadres = useLiveQuery(() => (vista === 'balance' ? cuadresLocales() : null), [vista], null);

  const nombreProducto = (id: string) => {
    const p = productos.find((x) => x.id === id);
    return p ? nombre(p) : undefined;
  };
  const nombreCliente = (id: string) => clientes.find((c) => c.id === id)?.nombre ?? t('utilidad.clienteEliminado');
  /*
   * What each figure the page works out means, and its sum with the period's own figures (the
   * owner, 2026-10-17: «tooltips … so users can see what every line actually means and how it's
   * calculated»): an account's line says its own (`explicacionCuenta`).
   */
  const ayudaMargen = resultados.margenBrutoBp === null ? '' : ` ${t('conta.ayuda.margen', { margen: pct(resultados.margenBrutoBp) })}`;
  const calculoBruto: PasoDelCalculo[] = [
    { etiqueta: t('conta.ingresos'), monto: dinero(resultados.totalIngresos) },
    { signo: '−', etiqueta: t('conta.costoVentas'), monto: dinero(resultados.totalCostos) },
    { signo: '=', etiqueta: t('conta.utilidadBruta'), monto: dinero(resultados.utilidadBruta) },
  ];
  const calculoNeto: PasoDelCalculo[] = [
    { etiqueta: t('conta.utilidadBruta'), monto: dinero(resultados.utilidadBruta) },
    { signo: '−', etiqueta: t('conta.gastos'), monto: dinero(resultados.totalGastos) },
    { signo: '=', etiqueta: t('conta.utilidadNeta'), monto: dinero(resultados.utilidadNeta) },
  ];
  const porProducto = useMemo(
    () => filasDichas(utilidadPorProducto(ventas, nombreProducto, rango), t),
    [ventas, productos, periodo, nombre, t],
  );
  const porCliente = useMemo(
    () => filasDichas(utilidadPorCliente(ventas, nombreCliente, rango), t),
    [ventas, clientes, periodo, t],
  );

  if (!puedeVer) {
    return (
      <Pagina titulo={t('conta.titulo')}>
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }

  return (
    <Pagina
      titulo={t('conta.titulo')}
      subtitulo={dias ?? t(opciones[indice]!.etiqueta)}
      ancho="amplio"
      acciones={
        <>
          {/* Cash between the safe and the bank, either way, and the owner's money in: from the books. */}
          {puedeMover && (
            <button type="button" className="btn-secundario gap-1.5 px-4 text-sm" data-conta-mover onClick={() => setHoja('mover')}>
              <Ico icono="transferencias" className="size-4" />
              {t('mover.titulo')}
            </button>
          )}
          {puedeAportar && (
            <button type="button" className="btn-secundario gap-1.5 px-4 text-sm" data-conta-aporte onClick={() => setHoja('aporte')}>
              <Ico icono="ingresar" className="size-4" />
              {t('aporte.titulo')}
            </button>
          )}
          {/* For the accountant: the same books, as spreadsheets. */}
          <Link to="/exportar" className="btn-secundario gap-1.5 px-4 text-sm">
            <Ico icono="exportar" className="size-4" />
            {t('exportar.corto')}
          </Link>
        </>
      }
    >
      {hoja === 'mover' && <MoverDinero onCerrar={() => setHoja(null)} />}
      {hoja === 'aporte' && <AporteDelDueno onCerrar={() => setHoja(null)} />}
      {/* The days, and beside them, or under them on a phone, the currency they are read in. */}
      <div className="mb-4 flex flex-wrap items-start gap-2">
        <div className="flex flex-wrap gap-2" data-guia="conta-periodo">
          {opciones.map((p, i) => (
            <button
              key={p.etiqueta}
              type="button"
              onClick={() => {
                elegirPeriodo(claveDe(p));
                setDeCaja(LOTE_CAJA);
              }}
              data-sin-icono="pestana"
              className={`rounded-lg px-3 py-1.5 text-sm transition ${
                !elegidas && i === indice ? 'bg-marca-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
              }`}
            >
              {t(p.etiqueta)}
            </button>
          ))}
          {/* Any days, and then the days it is: «Elegir fechas» as the histories have it. */}
          <button
            type="button"
            onClick={() => setCalendario(true)}
            aria-pressed={elegidas !== null}
            data-conta-fechas
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition ${
              elegidas ? 'bg-marca-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
            }`}
          >
            <Ico icono="calendario" className="size-4" />
            {dias ?? t('historial.periodo.elegir')}
          </button>
        </div>
        <ElegirMoneda moneda={moneda} onElegir={elegirMoneda} />
      </div>
      {calendario && (
        <Calendario
          desde={elegidas?.desde ?? ''}
          hasta={elegidas?.hasta ?? ''}
          hoyEs={hoyEs}
          onListo={(desde, hasta) => {
            setCalendario(false);
            setElegidas({ desde, hasta });
            setDeCaja(LOTE_CAJA);
          }}
          onCerrar={() => setCalendario(false)}
        />
      )}
      {moneda === 'USD' && (
        <p className="-mt-2 mb-4 text-xs text-slate-500" data-conta-tasa>
          {t('conta.enDolares', { tasa: formatFxRate(tasa) })}
        </p>
      )}

      <section className="tarjeta mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div data-guia="conta-ventas">
          <Cifra titulo={t('conta.ingresos')} ayuda={t('conta.ayuda.ingresos')} valor={resultados.totalIngresos} dinero={dinero} />
        </div>
        <div data-guia="conta-utilidad">
          <Cifra
            titulo={t('conta.utilidadBruta')}
            ayuda={t('conta.ayuda.utilidadBruta') + ayudaMargen}
            calculo={calculoBruto}
            valor={resultados.utilidadBruta}
            pie={pct(resultados.margenBrutoBp)}
            dinero={dinero}
          />
        </div>
        <div data-guia="conta-gastos">
          <Cifra titulo={t('conta.gastos')} ayuda={t('conta.ayuda.gastos')} valor={resultados.totalGastos} dinero={dinero} />
        </div>
        <Cifra
          titulo={t('conta.utilidadNeta')}
          ayuda={t('conta.ayuda.utilidadNeta')}
          calculo={calculoNeto}
          valor={resultados.utilidadNeta}
          resaltar={resultados.utilidadNeta < 0 ? 'mal' : 'bien'}
          dinero={dinero}
        />
      </section>

      {/* Four tabs do not fit on a 320px phone. Wrapping would break the underline in two, so
          the row scrolls instead. */}
      <div className="mb-4 flex gap-2 overflow-x-auto border-b border-slate-800">
        {VISTAS.map((v) => (
          <button
            key={v.clave}
            type="button"
            onClick={() => setVista(v.clave)}
            data-sin-icono="pestana"
            className={`-mb-px shrink-0 border-b-2 px-3 py-2 text-sm transition ${
              vista === v.clave
                ? 'border-marca-500 font-medium text-slate-100'
                : 'border-transparent text-slate-500 hover:border-slate-500 hover:text-slate-300'
            }`}
          >
            {t(v.nombre)}
          </button>
        ))}
      </div>

      {vista === 'resultados' && (
        <section className="tarjeta flex flex-col gap-4">
          <Bloque
            titulo={t('conta.ingresos')}
            ayuda={t('conta.ayuda.ingresos')}
            lineas={resultados.ingresos}
            total={resultados.totalIngresos}
            dinero={dinero}
          />
          <Bloque
            titulo={t('conta.costoVentas')}
            ayuda={t('conta.ayuda.costos')}
            lineas={resultados.costos}
            total={resultados.totalCostos}
            dinero={dinero}
          />
          <Renglon etiqueta={t('conta.utilidadBruta')} ayuda={t('conta.ayuda.utilidadBruta')} calculo={calculoBruto} monto={resultados.utilidadBruta} dinero={dinero} fuerte />
          <Bloque
            titulo={t('conta.gastos')}
            ayuda={t('conta.ayuda.gastos')}
            lineas={resultados.gastos}
            total={resultados.totalGastos}
            dinero={dinero}
          />
          <Renglon etiqueta={t('conta.utilidadNeta')} ayuda={t('conta.ayuda.utilidadNeta')} calculo={calculoNeto} monto={resultados.utilidadNeta} dinero={dinero} fuerte />
          {/* A sale on fiado is profit the day it is rung and money only when it is paid. */}
          {(fiado.crecio !== 0 || fiado.debenAlFinal !== 0) && (
            <p className="rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-300">
              {fiado.crecio > 0
                ? `${t('conta.fiadoSubio', { monto: dinero(fiado.crecio) })} `
                : fiado.crecio < 0
                  ? `${t('conta.fiadoBajo', { monto: dinero(-fiado.crecio as Centavos) })} `
                  : ''}
              {t('conta.fiadoTotal', { monto: dinero(fiado.debenAlFinal) })}
            </p>
          )}
          <p className="text-xs text-slate-500">{t('conta.explicacionRetiros')}</p>
        </section>
      )}

      {vista === 'balance' && (
        <section className="tarjeta flex flex-col gap-4">
          <Bloque titulo={t('conta.activo')} ayuda={t('conta.ayuda.activo')} lineas={balance.activos} total={balance.totalActivos} dinero={dinero} />
          <Bloque titulo={t('conta.pasivo')} ayuda={t('conta.ayuda.pasivo')} lineas={balance.pasivos} total={balance.totalPasivos} dinero={dinero} />
          <Bloque
            titulo={t('conta.patrimonio')}
            ayuda={t('conta.ayuda.patrimonio')}
            lineas={[
              ...balance.patrimonio,
              {
                codigo: '—',
                nombre: t('conta.utilidadEjercicio'),
                monto: balance.utilidadAcumulada,
                ayuda: t('conta.ayuda.utilidadEjercicio'),
              },
            ]}
            total={balance.totalPatrimonio}
            ayudaTotal={t('conta.ayuda.totalPatrimonio')}
            dinero={dinero}
          />
          <div
            className={`rounded-lg px-3 py-2 text-sm ${
              balance.descuadre === 0 ? 'bg-slate-800 text-slate-400' : 'bg-rose-500/10 text-rose-300'
            }`}
          >
            {/* In córdobas whatever is picked: centavos the books are off by would read US$ 0.00. */}
            {balance.descuadre === 0
              ? t('conta.balanceCuadra')
              : t('conta.descuadre', { monto: formatMoney(balance.descuadre) })}
          </div>
        </section>
      )}
      {vista === 'balance' && cuadres && <Cuadres lineas={cuadres} dinero={dinero} />}

      {vista === 'caja' && (
        <section className="tarjeta">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
            <span className="text-sm text-slate-400">
              <Explicacion
                texto={t('conta.ayuda.caja')}
                titulo={t('conta.caja')}
                calculo={[
                  { etiqueta: t('conta.ayuda.saldoInicial'), monto: dinero(caja.saldoInicial) },
                  { signo: '+', etiqueta: t('conta.ayuda.entradas'), monto: dinero(caja.totalEntradas) },
                  { signo: '−', etiqueta: t('conta.ayuda.salidas'), monto: dinero(caja.totalSalidas) },
                  { signo: '=', etiqueta: t('conta.ayuda.saldoFinal'), monto: dinero(caja.saldoFinal) },
                ]}
                className="hover:text-slate-200"
              >
                <ConMarca>
                  {t('conta.resumenCaja', {
                    inicial: dinero(caja.saldoInicial),
                    entradas: dinero(caja.totalEntradas),
                    salidas: dinero(caja.totalSalidas),
                  })}
                </ConMarca>
              </Explicacion>
            </span>
            <span className="tabular text-lg font-semibold">{dinero(caja.saldoFinal)}</span>
          </div>
          {caja.movimientos.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">{t('conta.sinMovimientos')}</p>
          ) : (
            <div className="-mx-2 overflow-x-auto">
              <table className="w-full min-w-md text-sm">
                <thead className="text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-2 py-1">{t('comun.fecha')}</th>
                    <th className="px-2 py-1">{t('conta.concepto')}</th>
                    <th className="px-2 py-1 text-right">{t('conta.entra')}</th>
                    <th className="px-2 py-1 text-right">{t('conta.sale')}</th>
                    <th className="px-2 py-1 text-right">{t('conta.saldo')}</th>
                  </tr>
                </thead>
                <tbody>
                  {caja.movimientos.slice(-deCaja).reverse().map((mv, i) => (
                    <tr key={`${mv.eventId}-${i}`} className="border-t border-slate-800">
                      <td className="tabular px-2 py-1.5 text-slate-500">{mv.fecha.slice(5)}</td>
                      <td className="px-2 py-1.5">
                        <span className="block truncate">{mv.concepto}</span>
                        <span className="block text-xs text-slate-600">
                          {etiquetaCuenta(mv.cuenta, mv.nombreCuenta, bancos)}
                        </span>
                      </td>
                      <td className="tabular px-2 py-1.5 text-right text-teal-300">
                        {mv.entrada ? dinero(mv.entrada, { symbol: false }) : ''}
                      </td>
                      <td className="tabular px-2 py-1.5 text-right text-amber-300">
                        {mv.salida ? dinero(mv.salida, { symbol: false }) : ''}
                      </td>
                      <td className="tabular px-2 py-1.5 text-right">
                        {dinero(mv.saldo, { symbol: false })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {caja.movimientos.length > deCaja && (
            <button type="button" className="btn-secundario mt-3 w-full text-sm" onClick={() => setDeCaja((n) => n + LOTE_CAJA)} data-conta-caja-mas>
              <Ico icono="desplegar" />
              {t('comun.verMas')}
            </button>
          )}
        </section>
      )}

      {vista === 'utilidad' && (
        <div className="flex flex-col gap-4">
          {/* Keyed by the period, as the cash book starts again at its first part with another. */}
          <TablaUtilidad key={`producto-${periodo.desde}-${periodo.hasta}`} titulo={t('conta.porProducto')} ayuda={t('conta.ayuda.porProducto')} filas={porProducto} dinero={dinero} muestraCantidad datos="producto" />
          <TablaUtilidad key={`cliente-${periodo.desde}-${periodo.hasta}`} titulo={t('conta.porCliente')} ayuda={t('conta.ayuda.porCliente')} filas={porCliente} dinero={dinero} datos="cliente" />
        </div>
      )}
    </Pagina>
  );
}

function Cifra({
  titulo,
  ayuda,
  calculo,
  valor,
  pie,
  resaltar,
  dinero,
}: {
  titulo: string;
  /** What it means and how it is worked out, in a bubble on its name. */
  ayuda: string;
  calculo?: readonly PasoDelCalculo[];
  valor: Centavos;
  pie?: string;
  resaltar?: 'bien' | 'mal';
  dinero: DineroDeLosLibros;
}) {
  const color = resaltar === 'mal' ? 'text-rose-300' : resaltar === 'bien' ? 'text-teal-300' : '';
  const cifra = dinero(valor);
  return (
    <div>
      <p className="text-xs text-slate-400">
        <Explicacion texto={ayuda} titulo={titulo} calculo={calculo} className="hover:text-slate-200">
          <ConMarca>{titulo}</ConMarca>
        </Explicacion>
      </p>
      {/*
        Switching «Hoy» for «Este mes», or córdobas for dollars, changes four figures at once, and
        four numbers quietly becoming four other numbers is a change somebody can miss entirely.
        The `key` makes each one a new element, so the pulse replays and the eye is told where to look.
      */}
      <p key={cifra} className={`tabular late mt-1 text-lg font-semibold ${color}`}>
        {cifra}
      </p>
      {pie && <p className="mt-0.5 text-xs text-slate-500">{pie}</p>}
    </div>
  );
}

/**
 * A line of the books as the bubble's button: the whole row lights up under the mouse, its words
 * and its figure where they were (`-mx-2` with `px-2`, so the light reaches past the words).
 */
const FILA = '-mx-2 flex w-[calc(100%+1rem)] justify-between gap-3 rounded-md px-2 hover:bg-white/5';

function Bloque({
  titulo,
  ayuda,
  lineas,
  total,
  ayudaTotal,
  dinero,
}: {
  titulo: string;
  /** What the group is, in a bubble on its name. */
  ayuda: string;
  /** A line the books do not keep says its own; an account's says the account's (`explicacionCuenta`). */
  lineas: readonly (LineaReporte & { ayuda?: string })[];
  total: Centavos;
  /** How the total is made, where it is more than the lines above it. */
  ayudaTotal?: string;
  dinero: DineroDeLosLibros;
}) {
  const t = useT();
  const bancos = useCuentasPuestas();
  return (
    <div>
      {/*
        A heading over its lines, as `.rotulo` is everywhere, or «Ingresos» and «Ventas» under it
        read alike. Its capitals go on the button too: a button does not take them from the heading.
      */}
      <h3 className="rotulo">
        <Explicacion texto={ayuda} titulo={titulo} className="uppercase hover:text-slate-300">
          <ConMarca>{titulo}</ConMarca>
        </Explicacion>
      </h3>
      {lineas.length === 0 ? (
        <p className="py-1 text-sm text-slate-600">{t('conta.sinMovimiento')}</p>
      ) : (
        <ul className="mt-1 flex flex-col gap-0.5">
          {lineas.map((l) => {
            const nombre = etiquetaCuenta(l.codigo, l.nombre, bancos);
            const ayudaLinea = l.ayuda ?? explicacionCuenta(l.codigo);
            const fila = (
              <>
                <span className="min-w-0 truncate text-slate-300 group-hover/explicacion:text-slate-100">{nombre}</span>
                <span className="tabular shrink-0">{dinero(l.monto, { symbol: false })}</span>
              </>
            );
            return (
              <li key={l.codigo + l.nombre} className="text-sm">
                {ayudaLinea ? (
                  <Explicacion
                    texto={ayudaLinea}
                    titulo={l.ayuda ? nombre : t('conta.ayuda.cuenta', { nombre, codigo: l.codigo })}
                    className={FILA}
                  >
                    {fila}
                  </Explicacion>
                ) : (
                  <span className="flex justify-between gap-3">{fila}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <Renglon
        etiqueta={t('conta.totalDe', { que: titulo.toLowerCase() })}
        ayuda={ayudaTotal ?? t('conta.ayuda.total', { grupo: titulo })}
        monto={total}
        dinero={dinero}
      />
    </div>
  );
}

/**
 * Each account with a detail of its own, against that detail (`reportes/cuadres.ts`): what clients
 * owe against «Cuentas por cobrar», what is owed on purchases and expenses against «Cuentas por
 * pagar», the shelves against «Inventario». They are written together, so they agree; one that
 * does not means the app has a fault, and the owner is told to say so. Stock may be off by the
 * centavos its rounding moves, and says so.
 */
/** What clients owe, what is owed, and stock: always there, at nothing too. The rest once in use. */
const SIEMPRE = new Set(['1300', '2100', '1200']);
/** The stock accounts: the only ones rounding may set a few centavos apart from their detail. */
const INVENTARIOS = new Set<Cuadre['detalle']>(['inventario', 'camion', 'transformacion']);

function Cuadres({ lineas, dinero }: { lineas: readonly Cuadre[]; dinero: DineroDeLosLibros }) {
  const t = useT();
  const bancos = useCuentasPuestas();
  const activas = lineas.filter((c) => SIEMPRE.has(c.cuenta) || c.libros !== 0 || c.suma !== 0);
  const sueltas = activas.filter((c) => !cuadra(c)).length;
  return (
    <section className="tarjeta mt-4">
      <h2 className="font-semibold">{t('conta.cuadres')}</h2>
      <p className={`mt-0.5 text-xs ${sueltas ? 'text-rose-300' : 'text-slate-500'}`}>
        {sueltas ? t('conta.cuadresNo', { n: sueltas }) : t('conta.cuadresAyuda')}
      </p>
      {activas.length === 0 ? (
        <p className="mt-2 text-sm text-slate-600">{t('conta.sinMovimiento')}</p>
      ) : (
        <ul className="mt-2 flex flex-col divide-y divide-slate-800">
          {activas.map((c) => (
            <li key={c.cuenta} className="py-1 text-sm">
              <Explicacion
                texto={t(INVENTARIOS.has(c.detalle) ? 'conta.ayuda.cuadreInventario' : 'conta.ayuda.cuadre')}
                titulo={etiquetaCuenta(c.cuenta, accountByCode(c.cuenta)?.name ?? c.cuenta, bancos)}
                calculo={[
                  { etiqueta: t('conta.ayuda.losLibros'), monto: dinero(c.libros) },
                  { etiqueta: t(`conta.detalle.${c.detalle}` as Clave), monto: dinero(c.suma) },
                ]}
                className={`${FILA} items-start py-1`}
              >
                <span className="block min-w-0">
                  <span className="block text-slate-300 group-hover/explicacion:text-slate-100">
                    {etiquetaCuenta(c.cuenta, accountByCode(c.cuenta)?.name ?? c.cuenta, bancos)}
                  </span>
                  <span className="block text-xs text-slate-500">{t(`conta.detalle.${c.detalle}` as Clave)}</span>
                </span>
                <span className="block max-w-[45%] shrink-0 text-right">
                  <span className="tabular block">{dinero(c.libros)}</span>
                  <span className={`block text-xs ${cuadra(c) ? 'text-slate-500' : 'text-rose-300'}`}>
                    {/* The rounding in córdobas whatever is picked: a few centavos would read US$ 0.00. */}
                    {c.diferencia === 0
                      ? t('conta.cuadra')
                      : cuadra(c)
                        ? t('conta.redondeo', { monto: formatMoney(Math.abs(c.diferencia) as Centavos) })
                        : t('conta.noCuadra', { monto: dinero(c.suma) })}
                  </span>
                </span>
              </Explicacion>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Renglon({
  etiqueta,
  ayuda,
  calculo,
  monto,
  dinero,
  fuerte,
}: {
  etiqueta: string;
  /** How it is worked out, in a bubble on the row. */
  ayuda: string;
  /** A figure the page works out, «Utilidad bruta»: its sum in the bubble, and an ⓘ to say so. */
  calculo?: readonly PasoDelCalculo[];
  monto: Centavos;
  dinero: DineroDeLosLibros;
  fuerte?: boolean;
}) {
  return (
    <div className={`mt-1 border-t border-slate-800 pt-1 ${fuerte ? 'text-base font-semibold' : 'text-sm text-slate-400'}`}>
      <Explicacion
        texto={ayuda}
        titulo={etiqueta}
        calculo={calculo}
        className={`${FILA} py-0.5 ${fuerte ? 'hover:text-white' : 'hover:text-slate-200'}`}
      >
        <span className="min-w-0">{calculo ? <ConMarca>{etiqueta}</ConMarca> : etiqueta}</span>
        {/* Whole, whatever the width: at 320 px «-C$ 2,535.44» broke after «-C$». */}
        <span className="tabular shrink-0 whitespace-nowrap">{dinero(monto)}</span>
      </Explicacion>
    </div>
  );
}

function TablaUtilidad({
  titulo,
  ayuda,
  filas,
  dinero,
  muestraCantidad = false,
  datos,
}: {
  titulo: string;
  /** What each column means and how it is worked out: the same for every row, so once, on the title. */
  ayuda: string;
  filas: readonly FilaUtilidad[];
  dinero: DineroDeLosLibros;
  muestraCantidad?: boolean;
  /** Which table, for a test to find it. */
  datos: string;
}) {
  const t = useT();
  const total = totalUtilidad(filas);
  const [cuantas, setCuantas] = useState(LOTE_UTILIDAD);
  return (
    <section className="tarjeta" data-conta-utilidad={datos}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-400">
          <Explicacion texto={ayuda} titulo={titulo} className="hover:text-slate-200">
            <ConMarca>{titulo}</ConMarca>
          </Explicacion>
        </h3>
        <span className="text-xs text-slate-500">
          {dinero(total.utilidad)} · {pct(total.margenBp)}
        </span>
      </div>
      {filas.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-500">{t('conta.sinVentasPeriodo')}</p>
      ) : (
        <div className="-mx-2 overflow-x-auto">
          <table className="w-full min-w-md text-sm">
            <thead className="text-left text-xs text-slate-500">
              <tr>
                <th className="px-2 py-1">{t('comun.nombre')}</th>
                {muestraCantidad && (
                  <th className="px-2 py-1 text-right">{t('conta.cantidadCorta')}</th>
                )}
                <th className="px-2 py-1 text-right">{t('conta.venta')}</th>
                <th className="px-2 py-1 text-right">{t('comun.costo')}</th>
                <th className="px-2 py-1 text-right">{t('comun.utilidad')}</th>
                <th className="px-2 py-1 text-right">{t('comun.margen')}</th>
              </tr>
            </thead>
            <tbody>
              {filas.slice(0, cuantas).map((f) => (
                <tr key={f.clave} className="border-t border-slate-800">
                  <td className="max-w-40 truncate px-2 py-1.5">{f.nombre}</td>
                  {muestraCantidad && (
                    <td className="tabular px-2 py-1.5 text-right text-slate-500">
                      {f.cantidad === null ? '' : formatQuantity(f.cantidad)}
                    </td>
                  )}
                  <td className="tabular px-2 py-1.5 text-right">
                    {dinero(f.ingreso, { symbol: false })}
                  </td>
                  <td className="tabular px-2 py-1.5 text-right text-slate-500">
                    {dinero(f.costo, { symbol: false })}
                  </td>
                  <td
                    className={`tabular px-2 py-1.5 text-right ${
                      f.utilidad < 0 ? 'text-rose-300' : ''
                    }`}
                  >
                    {dinero(f.utilidad, { symbol: false })}
                  </td>
                  <td className="tabular px-2 py-1.5 text-right text-slate-400">{pct(f.margenBp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Tapped, not scrolled into: the clients' table comes under the products'. */}
      {filas.length > cuantas && (
        <button type="button" className="btn-secundario mt-3 w-full text-sm" onClick={() => setCuantas((n) => n + LOTE_UTILIDAD)} data-conta-utilidad-mas>
          <Ico icono="desplegar" />
          {t('comun.verMas')}
        </button>
      )}
    </section>
  );
}
